/*
 * Copyright (c) 2021 EGT Digital - All rights reserved.
 *
 * File: FavoriteContext.tsx
 *
 * Written by Yuliyan Milenkov <yuliyan.milenkov@egt-digital.com> on  28.10.22 8:55 am.
 * Last modified: 28.10.22 8:55 am.
 *
 * More information at: https://egt-digital.com/
 */
import React, { createContext, useContext, useEffect, useState } from 'react';
import { BehaviorSubject } from 'rxjs';
import PortraitNewline from '../components/PortraitNewline';
import store from '../eventStore';

const defaultTranslations = {};

interface ITranslatorContextType {
    translations: any;
    setTranslations?: React.Dispatch<React.SetStateAction<any>>;
    subject?: BehaviorSubject<any>;
}

const TranslationsContext = createContext<ITranslatorContextType>({
    translations: defaultTranslations,
});

export const TranslationsProvider = ({ value: subject, children }: { value: BehaviorSubject<any>; children: any }) => {
    const [translations, setTranslations] = useState<any>(subject.value || defaultTranslations);

    useEffect(() => {
        const subscription = subject.subscribe(value => {
            if (value) {
                setTranslations(value);
            }
        });

        return () => subscription.unsubscribe();
    }, [subject]);

    return (
        <TranslationsContext.Provider value={{ translations, setTranslations, subject }}>
            {children}
        </TranslationsContext.Provider>
    );
};

export const useTranslations = (): [
    any,
    React.Dispatch<React.SetStateAction<any>> | ((translations: any) => void) | undefined,
] => {
    const { translations, setTranslations: setTranslationsReactDispatcher, subject } = useContext(TranslationsContext);
    const setTranslations = subject
        ? (newTranslations: any) => subject.next(newTranslations)
        : setTranslationsReactDispatcher;

    return [translations, setTranslations];
};

const replaceableTags = {
    '#$#': null,
    '##nl##': <br />,
    '##pnl##': <PortraitNewline />,
};
const plainHtmlTag = '##%##';

const parseAndConvertToHTML = (text: string, destinationArr: any[], tag: string) => {
    const parts = text.split(tag);

    parts.forEach((part, index) => {
        //// !!part check is for edge case if text start with tag ////
        if (index % 2 === 0 && !!part) {
            destinationArr.push(part);
        } else {
            // Extract tag name and attributes
            const tagRegex = /<([a-zA-Z0-9]+)\s*([^>]*)>([\s\S]*?)<\/\1>/;
            const tagMatch = part.match(tagRegex);

            if (tagMatch) {
                destinationArr.push(<span dangerouslySetInnerHTML={{ __html: part }}></span>);
            }
        }
    });
};
const flat = (arr: any[]): any[] => {
    try {
        return arr.reduce((acc: any[], curr: any[]) => {
            return [...acc, ...(Array.isArray(curr) ? curr : [curr])];
        }, []);
    } catch (flattErr) {
        return [];
    }
};

const replaceValuesForTag = (values: any[], tag: string, replaceParams?: any[]): any[] => {
    let replaceParamsIndex = 0;
    return values.map(val => {
        if (typeof val !== 'string' || val.indexOf(tag) === -1) {
            return val;
        } else {
            const translatableArray = val.split(tag);
            const translatedValues = translatableArray.reduce((acc: any[], curr: string, index: number) => {
                if (!curr.includes(plainHtmlTag)) {
                    acc.push(curr);
                } else {
                    parseAndConvertToHTML(curr, acc, plainHtmlTag);
                }

                if (replaceableTags[tag as keyof typeof replaceableTags]) {
                    if (index < translatableArray.length - 1) {
                        acc.push(replaceableTags[tag as keyof typeof replaceableTags]);
                    }
                } else if (replaceParamsIndex + 1 < translatableArray.length) {
                    if (replaceParams?.[replaceParamsIndex]) {
                        acc.push(replaceParams[replaceParamsIndex]);
                        replaceParamsIndex++;
                    }
                }
                return acc;
            }, []);
            return translatedValues;
        }
    });
};

const arrayHasValuesToParse = (values: any[]): boolean => {
    const replaceableTagsKeys = Object.keys(replaceableTags);
    return Boolean(
        values.find(value => {
            if (typeof value !== 'string') {
                return false;
            } else {
                return Boolean(replaceableTagsKeys.find(key => value.indexOf(key) !== -1));
            }
        }),
    );
};

const translate = (translations: any) => (value: string, ...restParams: any[]): any => {
    if (translations[value]) {
        const replaceableTagsKeys = Object.keys(replaceableTags);
        let translatableValue = [checkForRestrictions(translations, value)];
        let currentItteration = 0;

        while (arrayHasValuesToParse(translatableValue)) {
            translatableValue = flat(
                replaceValuesForTag(translatableValue, replaceableTagsKeys[currentItteration], restParams),
            );
            currentItteration++;
        }
        return translatableValue;
    } else if (store.DEFAULT_LANG.value[value]) {
        const replaceableTagsKeys = Object.keys(replaceableTags);
        let translatableValue = [checkForRestrictions(store.DEFAULT_LANG.value, value)];
        let currentItteration = 0;

        while (arrayHasValuesToParse(translatableValue)) {
            translatableValue = flat(
                replaceValuesForTag(translatableValue, replaceableTagsKeys[currentItteration], restParams),
            );
            currentItteration++;
        }
        return translatableValue;
    }
    return value;
};

const checkForRestrictions = (translations: any, translationKey: string): string => {
    if (store.SETTINGS.value?.restrictions?.length !== 0) {
        if (
            store.SETTINGS.value?.restrictions?.some(res => res === 'noJackpot') &&
            translations.hasOwnProperty('NO_JACKPOT.' + translationKey)
        ) {
            translationKey = 'NO_JACKPOT.' + translationKey;
            return translations[translationKey];
        }
    }

    return translations[translationKey];
};

export const useTranslator = (): ((value: string, ...restParams: any[]) => any) => {
    const [translations] = useTranslations();

    return translate(translations);
};
